import { useId, useState } from 'react';
import { useCombobox } from 'downshift';

import type { Padding } from '@shieldpay/theme-provider-ui/theme/types';

import { getInputFieldStyles } from '../input-field/get-input-field-styles';
import { getTextStyles } from '../text/text-styles';

import * as styles from './dropdown.css';

export { styles };

export type DropdownSize = 'medium' | 'large';

interface DropdownItem<Value> {
  /* NOTE: the label cannot use <FormattedMessage /> because the onlyText
   * function cannot extract the text.
   **/
  label: string;
  value: Value;
  disabled?: boolean;
}

export type DropdownItems<Value> = Array<DropdownItem<Value>>;

export interface GetSharedProps {
  size: DropdownSize;
  disabled: boolean;
  hasError?: boolean;
}

export const sizeConfig = {
  medium: { paddingY: 'baseNeg2' },
  large: { paddingY: 'base' }, // TODO: base should be basePos1, but I don't want to cause regressions on components that use this.
} as const;

export const getSharedProps = ({
  size,
  disabled,
  hasError = false,
}: GetSharedProps) => {
  const { stateStyles, boxProps, inputResetStyles } = getInputFieldStyles({
    disabled,
    error: !disabled && hasError,
  });

  return {
    inputResetStyles,
    stateStyles,
    boxProps,
    textStyles: getTextStyles({ variant: 'input' }),
    itemPadding: {
      text: {
        left: 'base',
        y: size === 'medium' ? 'baseNeg2' : 'basePos1',
      },
      chevron: {
        left: 'baseNeg3',
        right: 'baseNeg1',
      },
      checkBox: {
        left: 'baseNeg3',
      },
    } satisfies Record<'text' | 'chevron' | 'checkBox', Padding>,
  };
};

export type ControlledProps<Value> = {
  /* Supply a value to make component controlled
   * If you want a placeholder value use an empty string ('')
   **/
  onChange: (value: Value) => void;
  value?: Value | '';
  defaultValue?: never;
};

type UncontrolledProps<Value> = {
  onChange?: (value: Value) => void;
  value?: never;
  defaultValue?: Value | undefined;
};

export type ControlledArrayProps<Value> = {
  defaultValues?: Array<Value>;
};

type ControlledOrUncontrolled<Value = string> =
  | ControlledProps<Value>
  | UncontrolledProps<Value>;

export type UseDropdownOptions<Value = string> = {
  /* NOTE: the label on each item cannot use <FormattedMessage />
   * because the onlyText function cannot extract the text.
   **/
  items: DropdownItems<Value>;
  /*
   *  when using with conform we need to supply the id instead of using an internally generated one
   **/
  id?: string;
} & ControlledOrUncontrolled<Value>;

export type UseMultipleDropdownOptions<Value = string> = {
  /* NOTE: the label on each item cannot use <FormattedMessage />
   * because the onlyText function cannot extract the text.
   **/
  items: DropdownItems<Value>;
  /*
   *  when using with conform we need to supply the id instead of using an internally generated one
   **/
  id?: string;
} & ControlledArrayProps<Value>;

export const useDropdown = <Value>({
  id: externalId,
  items,
  onChange,
  value,
  defaultValue,
}: UseDropdownOptions<Value>) => {
  // id needs to be consistent between server and client
  const id = useId();

  const [menuItems, setMenuItems] = useState(() => items);

  const {
    isOpen,
    selectedItem,
    getToggleButtonProps,
    getLabelProps,
    getMenuProps,
    getInputProps: innerGetInputProps,
    highlightedIndex,
    getItemProps,
    inputValue,
    setInputValue,
  } = useCombobox<DropdownItem<Value | ''>>({
    // TODO does externalId even work?
    id: externalId || `dropdown-${id}`,
    items: menuItems,
    // This runs on each item whenever a keypress happens on the button
    // to allow us to match a string to the label, which will be a ReactNode
    // Do not use <FormattedMessage />, use formatMessage. See OPTIMUS-600 */
    itemToString: (item) => item?.label || '',
    onInputValueChange({ inputValue }) {
      setMenuItems(
        inputValue
          ? items.filter((item) =>
              item.label
                ?.toString()
                .toLowerCase()
                .includes(inputValue.toLowerCase()),
            )
          : items,
      );
    },
    initialSelectedItem: defaultValue
      ? items.find((item) => item.value === defaultValue)
      : undefined,
    onSelectedItemChange: ({ selectedItem }) => {
      if (selectedItem?.value && onChange) {
        onChange(selectedItem.value);
      }
    },
    selectedItem: value
      ? value === ''
        ? ({ value: '', label: '' } as const)
        : items.find((item) => item.value === value)
      : undefined,
  });

  const getInputProps = () =>
    innerGetInputProps({
      onFocus: () => {
        setInputValue('');
      },
      onBlur: () => {
        if (!inputValue) {
          setInputValue(selectedItem?.label || '');
        }
      },
    });

  return {
    menuItems,
    isOpen,
    selectedItem,
    getToggleButtonProps,
    getLabelProps,
    getMenuProps,
    getInputProps,
    highlightedIndex,
    getItemProps,
    inputValue,
  };
};

export const useMultipleDropdown = <Value>({
  id: externalId,
  items,
  defaultValues,
}: UseMultipleDropdownOptions<Value>) => {
  // id needs to be consistent between server and client
  const id = useId();

  const [menuItems, setMenuItems] = useState(() => items);
  const [selectedMenuItems, setSelectedMenuItems] = useState(() =>
    items.filter((item) =>
      defaultValues?.find((value) => value === item.value),
    ),
  );

  const {
    isOpen,
    getToggleButtonProps,
    getLabelProps,
    getMenuProps,
    getInputProps: innerGetInputProps,
    highlightedIndex,
    getItemProps,
    inputValue,
    setInputValue,
  } = useCombobox<DropdownItem<Value>>({
    // TODO does externalId even work?
    id: externalId || `dropdown-${id}`,
    items: menuItems,
    // there's no actual selection happening in the hook, we're doing it custom via selectedItems.
    selectedItem: null,
    // This runs on each item whenever a keypress happens on the button
    // to allow us to match a string to the label, which will be a ReactNode
    // Do not use <FormattedMessage />, use formatMessage. See OPTIMUS-600 */
    itemToString: (item) => item?.label || '',
    onInputValueChange({ inputValue }) {
      setMenuItems(
        inputValue
          ? items.filter((item) =>
              item.label
                ?.toString()
                .toLowerCase()
                .includes(inputValue.toLowerCase()),
            )
          : items,
      );
    },
    onSelectedItemChange({ selectedItem }) {
      if (!selectedItem) {
        return;
      }

      const index = selectedMenuItems.indexOf(selectedItem);
      if (index > 0) {
        setSelectedMenuItems([
          ...selectedMenuItems.slice(0, index),
          ...selectedMenuItems.slice(index + 1),
        ]);
      } else if (index === 0) {
        setSelectedMenuItems([...selectedMenuItems.slice(1)]);
      } else {
        setSelectedMenuItems([...selectedMenuItems, selectedItem]);
      }
    },
    stateReducer: (state, actionAndChanges) => {
      const { changes, type } = actionAndChanges;

      switch (type) {
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.ItemClick:
        case useCombobox.stateChangeTypes.ControlledPropUpdatedSelectedItem:
        case useCombobox.stateChangeTypes.InputClick:
          return {
            ...changes,
            isOpen: true, // keep menu open after selection.
            highlightedIndex: state.highlightedIndex,
            inputValue: state.inputValue, // don't add the item string as input value at selection.
          };
        case useCombobox.stateChangeTypes.InputBlur:
          return {
            ...changes,
            inputValue: '', // don't add the item string as input value at selection.
          };
        case useCombobox.stateChangeTypes.ToggleButtonClick:
          return {
            ...changes,
            isOpen: !state.isOpen,
            highlightedIndex: state.highlightedIndex,
            inputValue: '',
          };
        default:
          return changes;
      }
    },
  });

  const getInputProps = () =>
    innerGetInputProps({
      onBlur: () => {
        if (!inputValue) {
          setInputValue('');
        }
      },
    });

  return {
    menuItems,
    selectedMenuItems,
    isOpen,
    getToggleButtonProps,
    getLabelProps,
    getMenuProps,
    getInputProps,
    highlightedIndex,
    getItemProps,
    inputValue,
  };
};
